/**
 * Short share links for storefront products.
 *
 * The links creators copy from their hub used to spell out every deep-link param,
 * so a paid DM ran to 101 characters:
 *
 *   /test100?open_messages=true&open_message_checkout=true&product_id=10&product_type=dm
 *
 * They are now written as a single `p` token:
 *
 *   /test100?p=dm_10
 *
 * `expandShortProductLink()` turns the token back into the legacy params before
 * React mounts, so every existing reader (Influencer.js, Storefront.jsx,
 * InfluencerTabsBar.js, CoachingPreview.jsx, Community.js, ...) keeps reading the
 * params it always has. Nothing downstream needed changing, and the long
 * `?open_*` links creators have already pasted into bios and emails keep working
 * — this only changes what we *write*.
 *
 * Token grammar: `<kind>` on its own, or `<kind>_<id>`. A trailing `c` on the
 * kind means "open checkout", matching the linkType === 'checkout' branches in
 * the share-link builders.
 */

// Token -> the legacy params it expands to. `$` is replaced with the id.
const TOKEN_PARAMS = {
   sub:     { open_sub: 'true', single_tier: 'true', tier: '$' },
   subc:    { open_sub: 'true', single_tier: 'true', tier: '$', open_checkout: 'true' },
   mem:     { view_membership: 'true', id: '$' },
   comm:    { open_community: 'true', tab_id: '$' },
   commc:   { open_community_checkout: 'true', tab_id: '$' },
   course:  { open_collection: 'true', id: '$' },
   coursec: { open_buy_collection_checkout: 'true', open_collection: 'true', id: '$' },
   callt:   { open_coaching: 'true', tier: '$' },
   calltc:  { open_coaching: 'true', open_coaching_checkout: 'true', tier: '$' },
   dm:      { open_messages: 'true', open_message_checkout: 'true', product_id: '$', product_type: 'dm' },
   url:     { open_url_product: 'true', product_id: '$' },
   urlc:    { open_url_checkout: 'true', id: '$' },
   lead:    { open_lead_capture: 'true', id: '$' },
   dp:      { open_buy_now_checkout: 'true', id: '$' },
};

// The same kinds with no id — "open the section" rather than "open this product".
// `call` lives ONLY here: ?open_coaching=true opens the 1:1 coaching product,
// which has no per-item deep link (its readers honour open_coaching /
// open_coaching_checkout / tier and ignore any id). An id-bearing `call_<id>`
// would mint a param nothing reads, and coaching-call products deep-link via
// `url` instead — see the coachingCall branch in StorefrontProductList.js.
const BARE_TOKEN_PARAMS = {
   sub:    { open_sub: 'true' },
   mem:    { view_membership: 'true' },
   comm:   { open_community: 'true' },
   course: { open_collection: 'true' },
   call:   { open_coaching: 'true' },
   dm:     { open_messages: 'true' },
};

/**
 * Own-property lookup. A plain `TOKEN_PARAMS[kind]` also finds inherited members
 * like `constructor` and `toString`, which would make `?p=constructor_1` look
 * like a valid token and get silently consumed instead of left alone.
 */
function tokenParams(table, kind) {
   return Object.prototype.hasOwnProperty.call(table, kind) ? table[kind] : null;
}

export const SHORT_LINK_PARAM = 'p';

/**
 * Builds the `p` token. Returns '' when the kind isn't one we can shorten, so
 * callers can fall back to their long-form URL.
 *
 * @param {string} kind  key of TOKEN_PARAMS / BARE_TOKEN_PARAMS
 * @param {string|number|null} [id]
 * @returns {string}
 */
export function shortProductToken(kind, id = null) {
   const key = String(kind || '').trim();
   if (!key) return '';

   const hasId = id !== null && id !== undefined && String(id).trim() !== '';
   if (!hasId) return tokenParams(BARE_TOKEN_PARAMS, key) ? key : '';
   if (!tokenParams(TOKEN_PARAMS, key)) return '';

   return `${key}_${String(id).trim()}`;
}

/**
 * Builds a full short share link, e.g. buildShortProductLink(origin, 'test100', 'dm', 10)
 * -> 'https://withme.so/test100?p=dm_10'.
 *
 * Returns '' when the kind can't be shortened, so the caller keeps its long URL.
 *
 * @returns {string}
 */
export function buildShortProductLink(origin, influencerSlug, kind, id = null) {
   const token = shortProductToken(kind, id);
   if (!token) return '';
   // origin/slug are interpolated even when blank, matching what the long-form
   // builders did, so a missing slug fails the same way it always has.
   return `${origin}/${influencerSlug}?${SHORT_LINK_PARAM}=${token}`;
}

/**
 * Turns a `p` token into the legacy params. Returns null when the token is
 * missing or unrecognised.
 *
 * @param {string} token
 * @returns {Object|null}
 */
export function decodeShortProductToken(token) {
   const raw = String(token || '').trim();
   if (!raw) return null;

   const separator = raw.indexOf('_');
   const kind = separator === -1 ? raw : raw.slice(0, separator);
   const id = separator === -1 ? '' : raw.slice(separator + 1);

   if (!id) {
      const bare = tokenParams(BARE_TOKEN_PARAMS, kind);
      return bare ? { ...bare } : null;
   }

   const template = tokenParams(TOKEN_PARAMS, kind);
   if (!template) return null;

   const params = {};
   Object.keys(template).forEach((name) => {
      params[name] = template[name] === '$' ? id : template[name];
   });
   return params;
}

/**
 * Rewrites a query string, replacing the `p` token with the params it stands for.
 * Params already present win, so an explicit `?open_sub=true` is never clobbered,
 * and everything else on the URL (promo_code, aff, utm_*, ...) is preserved.
 *
 * Returns null when there is nothing to expand.
 *
 * @param {string} search  e.g. '?p=dm_10'
 * @returns {string|null}  e.g. 'open_messages=true&open_message_checkout=true&...'
 */
export function expandShortProductSearch(search) {
   let params;
   try {
      params = new URLSearchParams(search || '');
   } catch (e) {
      return null;
   }

   const token = params.get(SHORT_LINK_PARAM);
   if (!token) return null;

   const expanded = decodeShortProductToken(token);
   if (!expanded) return null;

   params.delete(SHORT_LINK_PARAM);
   Object.keys(expanded).forEach((name) => {
      if (!params.has(name)) params.set(name, expanded[name]);
   });

   return params.toString();
}

/**
 * Expands a short link in the address bar into the legacy params.
 *
 * Must run BEFORE React Router mounts — `replaceState` on its own doesn't update
 * the router's internal searchParams state, so calling this from a component
 * would leave the readers looking at the un-expanded URL. index.js calls it at
 * module load, ahead of root.render().
 *
 * @returns {boolean} whether the URL was rewritten
 */
export function expandShortProductLink() {
   if (typeof window === 'undefined' || !window.location) return false;

   try {
      const expanded = expandShortProductSearch(window.location.search);
      if (expanded === null) return false;

      const query = expanded ? `?${expanded}` : '';
      window.history.replaceState(
         window.history.state,
         '',
         `${window.location.pathname}${query}${window.location.hash}`
      );
      return true;
   } catch (e) {
      return false;
   }
}

export default buildShortProductLink;
